import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faSpinner, faThumbtack, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch, formatDateTime } from '../../utils/http';

interface Announcement {
  id: string;
  title: string;
  body: string;
  isPinned: boolean;
  authorName: string;
  createdAt: string;
}

interface Props {
  learnershipId: string;
  canManage: boolean;
}

export default function AnnouncementsPanel({ learnershipId, canManage }: Props) {
  const { accessToken } = useAuth();
  const [items, setItems] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const res = await authFetch<Announcement[]>(accessToken, `/api/community/courses/${learnershipId}/announcements`);
    if (res.ok && res.data) {
      setItems(res.data);
      setError('');
    } else {
      setError(res.message || 'Failed to load announcements.');
    }
    setIsLoading(false);
  }, [accessToken, learnershipId]);

  useEffect(() => {
    load();
  }, [load]);

  const post = async () => {
    setError('');
    if (!title.trim() || !body.trim()) {
      setError('Title and message are required.');
      return;
    }
    setIsSaving(true);
    const res = await authFetch(accessToken, `/api/community/courses/${learnershipId}/announcements`, {
      method: 'POST',
      body: { title, body, isPinned },
    });
    setIsSaving(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setTitle('');
    setBody('');
    setIsPinned(false);
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this announcement?')) return;
    const res = await authFetch(accessToken, `/api/community/announcements/${id}`, { method: 'DELETE' });
    if (!res.ok) setError(res.message);
    else load();
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="card-surface p-5 space-y-3">
          <h3 className="text-sm font-bold text-brand-950">Post an announcement</h3>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="Message to all learners on this course"
            className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-brand-900/70">
              <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />
              Pin to top
            </label>
            <button
              onClick={post}
              disabled={isSaving}
              className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={isSaving ? faSpinner : faPlus} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
              Post
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="card-surface p-12 text-center">
          <FontAwesomeIcon icon={faBullhorn} className="h-12 w-12 text-brand-300 mb-4" />
          <p className="text-brand-900/60">No announcements yet.</p>
        </div>
      ) : (
        items.map((a) => (
          <div key={a.id} className={`card-surface p-5 ${a.isPinned ? 'border-l-4 border-brand-600' : ''}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h4 className="text-base font-bold text-brand-950 flex items-center gap-2">
                  {a.isPinned && <FontAwesomeIcon icon={faThumbtack} className="h-3.5 w-3.5 text-brand-600" />}
                  {a.title}
                </h4>
                <p className="text-xs text-brand-900/50 mt-0.5">
                  {a.authorName} · {formatDateTime(a.createdAt)}
                </p>
              </div>
              {canManage && (
                <button onClick={() => remove(a.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0" title="Delete">
                  <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <p className="text-sm text-brand-900/80 mt-3 whitespace-pre-wrap">{a.body}</p>
          </div>
        ))
      )}
    </div>
  );
}
